import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Building,
  Sparkles,
  FileSignature,
  HardDrive,
  ShieldAlert,
  ArrowRight,
  Zap,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@homy/ui";
import { useUpgradeModal } from "@/core/stores/upgradeModalStore";

const QUOTA_METRICS = {
  units: {
    icon: Building,
    labelKey: "settings:memberships.upgradeModal.quotaTypes.units",
    fallbackLabel: "Units",
    badgeClasses: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  },
  ai_tokens: {
    icon: Sparkles,
    labelKey: "settings:memberships.upgradeModal.quotaTypes.ai_tokens",
    fallbackLabel: "AI Tokens",
    badgeClasses: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  },
  homysign_envelopes: {
    icon: FileSignature,
    labelKey: "settings:memberships.upgradeModal.quotaTypes.homysign_envelopes",
    fallbackLabel: "HomySign Envelopes",
    badgeClasses: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  },
  storage: {
    icon: HardDrive,
    labelKey: "settings:memberships.upgradeModal.quotaTypes.storage",
    fallbackLabel: "Document Storage",
    badgeClasses: "text-purple-400 bg-purple-500/10 border-purple-500/30",
  },
};

export default function UpgradeModal() {
  const { t } = useTranslation(["settings", "common"]);
  const navigate = useNavigate();
  const { isOpen, data, closeUpgradeModal } = useUpgradeModal();

  const quotaType = data?.quota_type || data?.quotaType || "generic";
  const quotaConfig = QUOTA_METRICS[quotaType] || {
    icon: ShieldAlert,
    labelKey: "settings:memberships.upgradeModal.quotaTypes.generic",
    fallbackLabel: "Quota",
    badgeClasses: "text-primary bg-primary/10 border-primary/30",
  };

  const IconComponent = quotaConfig.icon;
  const quotaLabel = t(quotaConfig.labelKey, quotaConfig.fallbackLabel);

  const currentUsage = data?.current_usage ?? data?.currentUsage;
  const usageLimit = data?.limit ?? data?.usageLimit;
  const hasUsageStats =
    currentUsage !== undefined &&
    currentUsage !== null &&
    usageLimit !== undefined &&
    usageLimit !== null;
  const recommendedTier = data?.upgrade_tier || data?.upgradeTier;
  const rawMessage =
    typeof data?.message === "string" && data.message
      ? data.message
      : typeof data?.detail === "string" && data.detail
        ? data.detail
        : typeof data?.detail?.message === "string" && data.detail.message
          ? data.detail.message
          : null;

  const handleNavigateToPlans = () => {
    closeUpgradeModal();
    navigate("/settings/plans");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeUpgradeModal()}>
      <DialogContent
        data-testid="upgrade-modal"
        className="max-w-md border border-border bg-card p-6 text-foreground shadow-2xl sm:rounded-2xl"
      >
        <DialogHeader className="space-y-3 text-left">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary border border-primary/30 shrink-0">
              <IconComponent className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold font-display tracking-tight text-foreground">
                {t("settings:memberships.upgradeModal.title", "Upgrade Your Plan")}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant="outline"
                  className={`text-caption font-semibold px-2 py-0.5 border ${quotaConfig.badgeClasses}`}
                >
                  {quotaLabel} · {t("settings:memberships.upgradeModal.quotaExceededBadge", "Limit Reached")}
                </Badge>
              </div>
            </div>
          </div>

          <DialogDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
            {rawMessage ||
              t(
                "settings:memberships.upgradeModal.defaultMessage",
                "You have reached your tier limit. Upgrade your plan to continue using this feature without interruption."
              )}
          </DialogDescription>
        </DialogHeader>

        {/* Usage Telemetry & Recommended Tier */}
        <div className="space-y-3 py-1">
          {hasUsageStats && (
            <div className="rounded-xl border border-border bg-background/80 p-3.5 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-medium">
                  {t("settings:memberships.upgradeModal.usageLabel", "Usage")}
                </span>
                <span className="font-semibold text-foreground tabular-nums">
                  {currentUsage} / {usageLimit} {quotaLabel.toLowerCase()}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{
                    width: `${Math.min(100, Math.max(0, (Number(currentUsage) / Math.max(1, Number(usageLimit))) * 100))}%`,
                  }}
                />
              </div>
            </div>
          )}

          {recommendedTier && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/20 text-primary shrink-0">
                  <Zap className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">
                    {t("settings:memberships.upgradeModal.recommendedTier", "Recommended Tier: {{tier}}", {
                      tier: recommendedTier,
                    })}
                  </p>
                  <p className="text-caption text-muted-foreground truncate">
                    {t(
                      "settings:memberships.upgradeModal.recommendedTierSubtitle",
                      "Unlock higher capacity and advanced automation."
                    )}
                  </p>
                </div>
              </div>
              <Badge className="bg-primary text-primary-foreground font-bold text-xs uppercase px-2.5 py-0.5 shrink-0">
                {recommendedTier}
              </Badge>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-2 pt-2 border-t border-border">
          <Button
            type="button"
            variant="ghost"
            className="min-h-[44px] w-full sm:w-auto px-4 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={closeUpgradeModal}
          >
            {t("settings:memberships.upgradeModal.dismiss", "Dismiss")}
          </Button>
          <Button
            type="button"
            className="min-h-[44px] w-full sm:w-auto px-5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-1.5 shadow-md"
            onClick={handleNavigateToPlans}
          >
            <span>{t("settings:memberships.upgradeModal.viewPlansCta", "View Plans & Upgrade")}</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
